import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, LogOutIcon, CheckCircleIcon, XCircleIcon, ClockIcon, EditIcon, SaveIcon, SendIcon, GlobeIcon, GithubIcon, TwitterIcon, LinkedinIcon, MapPinIcon, PlusIcon, TrashIcon, LinkIcon, AwardIcon, FolderIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { TextArea } from '../../components/ui/TextArea';
interface Skill {
  name: string;
  certificationUrl?: string;
}
interface Project {
  title: string;
  url: string;
  description?: string;
}
interface DeveloperProfile {
  name: string;
  title: string;
  bio: string;
  skills: Skill[];
  projects: Project[];
  openTo: string[];
  location: string;
  country: string;
  rateMin: number;
  rateMax: number;
  rateCurrency: string;
  website: string;
  github: string;
  twitter: string;
  linkedin: string;
  avatar: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  availability: 'available' | 'unavailable';
  rejectionReason?: string;
}
const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<DeveloperProfile | null>(null);
  const [formData, setFormData] = useState<DeveloperProfile>({
    name: '',
    title: '',
    bio: '',
    skills: [],
    projects: [],
    openTo: [],
    location: '',
    country: '',
    rateMin: 50,
    rateMax: 100,
    rateCurrency: 'USD',
    website: '',
    github: '',
    twitter: '',
    linkedin: '',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    status: 'draft',
    availability: 'available'
  });
  const [newSkill, setNewSkill] = useState({
    name: '',
    certificationUrl: ''
  });
  const [newProject, setNewProject] = useState({
    title: '',
    url: '',
    description: ''
  });
  const [avatarUploadMethod, setAvatarUploadMethod] = useState<'upload' | 'url'>('upload');
  useEffect(() => {
    // Check if user is authenticated
    const isAuth = localStorage.getItem('developerAuth');
    if (!isAuth) {
      navigate('/developer/login');
      return;
    }
    // Load profile from localStorage
    const savedProfile = localStorage.getItem('developerProfile');
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile);
      setProfile(parsedProfile);
      setFormData(parsedProfile);
    } else {
      // New user - set name from signup
      const name = localStorage.getItem('developerName') || '';
      setFormData(prev => ({
        ...prev,
        name
      }));
      setIsEditing(true);
    }
  }, [navigate]);
  const handleLogout = () => {
    localStorage.removeItem('developerAuth');
    localStorage.removeItem('developerEmail');
    localStorage.removeItem('developerName');
    navigate('/developer/login');
  };
  const handleSaveDraft = () => {
    const updatedProfile = {
      ...formData,
      status: 'draft' as const
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setIsEditing(false);
  };
  const handleSubmitForApproval = () => {
    if (!formData.name || !formData.title || !formData.bio) {
      alert('Please fill in all required fields (Name, Title, Bio)');
      return;
    }
    if (formData.skills.length === 0) {
      alert('Please add at least one skill with certification');
      return;
    }
    const updatedProfile = {
      ...formData,
      status: 'pending' as const
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setIsEditing(false);
  };
  const handleToggleAvailability = () => {
    if (!profile) return;
    const newAvailability = profile.availability === 'available' ? 'unavailable' : 'available';
    const updatedProfile = {
      ...profile,
      availability: newAvailability
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setFormData(updatedProfile);
  };
  const handleAddSkill = () => {
    if (newSkill.name.trim() && !formData.skills.some(s => s.name === newSkill.name.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, {
          name: newSkill.name.trim(),
          certificationUrl: newSkill.certificationUrl.trim() || undefined
        }]
      });
      setNewSkill({
        name: '',
        certificationUrl: ''
      });
    }
  };
  const handleRemoveSkill = (skillName: string) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s.name !== skillName)
    });
  };
  const handleAddProject = () => {
    if (newProject.title.trim() && newProject.url.trim()) {
      setFormData({
        ...formData,
        projects: [...formData.projects, {
          title: newProject.title.trim(),
          url: newProject.url.trim(),
          description: newProject.description.trim() || undefined
        }]
      });
      setNewProject({
        title: '',
        url: '',
        description: ''
      });
    }
  };
  const handleRemoveProject = (index: number) => {
    setFormData({
      ...formData,
      projects: formData.projects.filter((_, i) => i !== index)
    });
  };
  const handleOpenToChange = (option: string) => {
    if (formData.openTo.includes(option)) {
      setFormData({
        ...formData,
        openTo: formData.openTo.filter(o => o !== option)
      });
    } else {
      setFormData({
        ...formData,
        openTo: [...formData.openTo, option]
      });
    }
  };
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          avatar: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full" data-id="element-343">
            Draft
          </span>;
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full flex items-center" data-id="element-344">
            <ClockIcon className="h-4 w-4 mr-1" data-id="element-345" />
            Pending Review
          </span>;
      case 'approved':
        return <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full flex items-center" data-id="element-346">
            <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-347" />
            Approved
          </span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full flex items-center" data-id="element-348">
            <XCircleIcon className="h-4 w-4 mr-1" data-id="element-349" />
            Rejected
          </span>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-350">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm" data-id="element-351">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-352">
          <div className="flex justify-between items-center py-4" data-id="element-353">
            <div className="flex items-center" data-id="element-354">
              <div className="w-10 h-10 bg-[#d73b3e] rounded-full flex items-center justify-center mr-3" data-id="element-355">
                <UserIcon className="h-6 w-6 text-white" data-id="element-356" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-357">
                Developer Dashboard
              </h1>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="flex items-center" data-id="element-358">
              <LogOutIcon className="h-5 w-5 mr-2" data-id="element-359" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-360">
        {/* Status Banner */}
        {profile && <div className="mb-8" data-id="element-361">
            <Card className="bg-white" data-id="element-362">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-363">
                <div className="flex items-center" data-id="element-364">
                  <div className="mr-4" data-id="element-365">
                    <img src={profile.avatar} alt={profile.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#FF3B30]" data-id="element-366" />
                  </div>
                  <div data-id="element-367">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-368">
                      {profile.name}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-369">
                      {profile.title || 'No title set'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-370">
                  {getStatusBadge(profile.status)}
                  {profile.status === 'approved' && <Button variant={profile.availability === 'available' ? 'primary' : 'outline'} size="sm" onClick={handleToggleAvailability} data-id="element-371">
                      {profile.availability === 'available' ? 'Available' : 'Unavailable'}
                    </Button>}
                </div>
              </div>
            </Card>
          </div>}
        {/* Rejection Message */}
        {profile?.status === 'rejected' && profile.rejectionReason && <div className="mb-8" data-id="element-372">
            <Card className="bg-red-50 border-red-200" data-id="element-373">
              <div className="flex items-start" data-id="element-374">
                <XCircleIcon className="h-5 w-5 text-red-600 mr-3 mt-0.5" data-id="element-375" />
                <div data-id="element-376">
                  <h3 className="text-sm font-semibold text-red-900 mb-1" data-id="element-377">
                    Profile Rejected
                  </h3>
                  <p className="text-sm text-red-700" data-id="element-378">
                    {profile.rejectionReason}
                  </p>
                  <p className="text-sm text-red-600 mt-2" data-id="element-379">
                    Please update your profile and resubmit for approval.
                  </p>
                </div>
              </div>
            </Card>
          </div>}
        {/* Pending Message */}
        {profile?.status === 'pending' && <div className="mb-8" data-id="element-380">
            <Card className="bg-yellow-50 border-yellow-200" data-id="element-381">
              <div className="flex items-start" data-id="element-382">
                <ClockIcon className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" data-id="element-383" />
                <div data-id="element-384">
                  <h3 className="text-sm font-semibold text-yellow-900 mb-1" data-id="element-385">
                    Profile Under Review
                  </h3>
                  <p className="text-sm text-yellow-700" data-id="element-386">
                    Your profile has been submitted and is currently being
                    reviewed by our team. You will be notified once the review
                    is complete.
                  </p>
                </div>
              </div>
            </Card>
          </div>}
        {/* Profile Form */}
        <Card className="bg-white" data-id="element-387">
          <div className="flex justify-between items-center mb-6" data-id="element-388">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-389">
              Profile Information
            </h3>
            {profile && !isEditing && profile.status !== 'pending' && <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="flex items-center" data-id="element-390">
                <EditIcon className="h-4 w-4 mr-1" data-id="element-391" />
                Edit Profile
              </Button>}
          </div>
          {isEditing ? <div className="space-y-6" data-id="element-392">
              {/* Avatar */}
              <div data-id="element-393">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-394">
                  Profile Picture <span className="text-[#FF3B30]" data-id="element-395">*</span>
                </label>
                <div className="flex items-start gap-4" data-id="element-396">
                  <img src={formData.avatar} alt="Avatar preview" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" data-id="element-397" />
                  <div className="flex-1" data-id="element-398">
                    {/* Toggle between upload and URL */}
                    <div className="flex gap-2 mb-3" data-id="element-399">
                      <button type="button" onClick={() => setAvatarUploadMethod('upload')} className={`px-3 py-1 text-sm rounded ${avatarUploadMethod === 'upload' ? 'bg-[#d73b3e] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-400">
                        Upload File
                      </button>
                      <button type="button" onClick={() => setAvatarUploadMethod('url')} className={`px-3 py-1 text-sm rounded ${avatarUploadMethod === 'url' ? 'bg-[#d73b3e] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-401">
                        Enter URL
                      </button>
                    </div>
                    {avatarUploadMethod === 'upload' ? <div data-id="element-402">
                        <input type="file" accept="image/*" onChange={handleAvatarUpload} className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded file:border-0
                            file:text-sm file:font-medium
                            file:bg-[#d73b3e] file:text-white
                            hover:file:bg-[#c02f32]
                            cursor-pointer" data-id="element-403" />
                        <p className="text-xs text-gray-500 mt-1" data-id="element-404">
                          Upload an image (max 5MB, JPG, PNG, GIF)
                        </p>
                      </div> : <div data-id="element-405">
                        <Input value={formData.avatar} onChange={e => setFormData({
                    ...formData,
                    avatar: e.target.value
                  })} placeholder="Enter image URL (e.g., https://example.com/avatar.jpg)" data-id="element-406" />
                        <p className="text-xs text-gray-500 mt-1" data-id="element-407">
                          Enter a direct link to your profile picture
                        </p>
                      </div>}
                  </div>
                </div>
              </div>
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-408">
                <Input label="Full Name" required value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="John Doe" data-id="element-409" />
                <Input label="Professional Title" required value={formData.title} onChange={e => setFormData({
              ...formData,
              title: e.target.value
            })} placeholder="Senior Laravel Developer" data-id="element-410" />
              </div>
              <TextArea label="Bio" required value={formData.bio} onChange={e => setFormData({
            ...formData,
            bio: e.target.value
          })} placeholder="Tell us about your experience and expertise..." rows={4} data-id="element-411" />
              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-412">
                <Input label="City/Location" value={formData.location} onChange={e => setFormData({
              ...formData,
              location: e.target.value
            })} placeholder="San Francisco" data-id="element-413" />
                <Input label="Country" value={formData.country} onChange={e => setFormData({
              ...formData,
              country: e.target.value
            })} placeholder="United States" data-id="element-414" />
              </div>
              {/* Rate */}
              <div data-id="element-415">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-416">
                  Hourly Rate Range
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-417">
                  <Input type="number" value={formData.rateMin} onChange={e => setFormData({
                ...formData,
                rateMin: Number(e.target.value)
              })} placeholder="50" min="0" data-id="element-418" />
                  <Input type="number" value={formData.rateMax} onChange={e => setFormData({
                ...formData,
                rateMax: Number(e.target.value)
              })} placeholder="100" min="0" data-id="element-419" />
                  <select value={formData.rateCurrency} onChange={e => setFormData({
                ...formData,
                rateCurrency: e.target.value
              })} className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:border-[#FF3B30]" data-id="element-420">
                    <option value="USD" data-id="element-421">USD</option>
                    <option value="EUR" data-id="element-422">EUR</option>
                    <option value="GBP" data-id="element-423">GBP</option>
                  </select>
                </div>
              </div>
              {/* Skills with Certifications */}
              <div data-id="element-424">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-425">
                  Skills & Certifications{' '}
                  <span className="text-[#FF3B30]" data-id="element-426">*</span>
                </label>
                <div className="space-y-3 mb-3" data-id="element-427">
                  <Input value={newSkill.name} onChange={e => setNewSkill({
                ...newSkill,
                name: e.target.value
              })} placeholder="Skill name (e.g., Laravel, PHP, Vue.js)" data-id="element-428" />
                  <div className="flex gap-2" data-id="element-429">
                    <Input value={newSkill.certificationUrl} onChange={e => setNewSkill({
                  ...newSkill,
                  certificationUrl: e.target.value
                })} placeholder="Certification URL (optional)" className="flex-1" data-id="element-430" />
                    <Button type="button" variant="primary" onClick={handleAddSkill} data-id="element-431">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-432" />
                      Add
                    </Button>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-433">
                  {formData.skills.map(skill => <div key={skill.name} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg" data-id="element-434">
                      <div className="flex items-center gap-2 flex-1" data-id="element-435">
                        <span className="font-medium text-blue-900" data-id="element-436">
                          {skill.name}
                        </span>
                        {skill.certificationUrl && <a href={skill.certificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800 text-sm" data-id="element-437">
                            <AwardIcon className="h-3 w-3 mr-1" data-id="element-438" />
                            Certificate
                          </a>}
                      </div>
                      <button type="button" onClick={() => handleRemoveSkill(skill.name)} className="text-red-500 hover:text-red-700" data-id="element-439">
                        <TrashIcon className="h-4 w-4" data-id="element-440" />
                      </button>
                    </div>)}
                </div>
              </div>
              {/* Projects */}
              <div data-id="element-441">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-442">
                  Projects
                </label>
                <div className="space-y-3 mb-3" data-id="element-443">
                  <Input value={newProject.title} onChange={e => setNewProject({
                ...newProject,
                title: e.target.value
              })} placeholder="Project title" data-id="element-444" />
                  <Input value={newProject.url} onChange={e => setNewProject({
                ...newProject,
                url: e.target.value
              })} placeholder="Project URL" data-id="element-445" />
                  <div className="flex gap-2" data-id="element-446">
                    <Input value={newProject.description} onChange={e => setNewProject({
                  ...newProject,
                  description: e.target.value
                })} placeholder="Brief description (optional)" className="flex-1" data-id="element-447" />
                    <Button type="button" variant="primary" onClick={handleAddProject} data-id="element-448">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-449" />
                      Add
                    </Button>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-450">
                  {formData.projects.map((project, index) => <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg" data-id="element-451">
                      <div className="flex-1" data-id="element-452">
                        <div className="flex items-center gap-2" data-id="element-453">
                          <FolderIcon className="h-4 w-4 text-gray-400" data-id="element-454" />
                          <span className="font-medium text-gray-900" data-id="element-455">
                            {project.title}
                          </span>
                        </div>
                        <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center mt-1" data-id="element-456">
                          <LinkIcon className="h-3 w-3 mr-1" data-id="element-457" />
                          {project.url}
                        </a>
                        {project.description && <p className="text-sm text-gray-600 mt-1" data-id="element-458">
                            {project.description}
                          </p>}
                      </div>
                      <button type="button" onClick={() => handleRemoveProject(index)} className="text-red-500 hover:text-red-700 ml-2" data-id="element-459">
                        <TrashIcon className="h-4 w-4" data-id="element-460" />
                      </button>
                    </div>)}
                </div>
              </div>
              {/* Open To */}
              <div data-id="element-461">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-462">
                  Open To
                </label>
                <div className="flex flex-wrap gap-3" data-id="element-463">
                  {['Full-time', 'Part-time', 'Contract', 'Freelance', 'Remote'].map(option => <label key={option} className="flex items-center cursor-pointer" data-id="element-464">
                      <input type="checkbox" checked={formData.openTo.includes(option)} onChange={() => handleOpenToChange(option)} className="h-4 w-4 text-[#FF3B30] focus:ring-[#FF3B30] border-gray-300 rounded" data-id="element-465" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-466">
                        {option}
                      </span>
                    </label>)}
                </div>
              </div>
              {/* Links */}
              <div data-id="element-467">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-468">
                  Social Links
                </label>
                <div className="space-y-3" data-id="element-469">
                  <div className="flex items-center" data-id="element-470">
                    <GlobeIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-471" />
                    <Input value={formData.website} onChange={e => setFormData({
                  ...formData,
                  website: e.target.value
                })} placeholder="https://yourwebsite.com" className="flex-1" data-id="element-472" />
                  </div>
                  <div className="flex items-center" data-id="element-473">
                    <GithubIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-474" />
                    <Input value={formData.github} onChange={e => setFormData({
                  ...formData,
                  github: e.target.value
                })} placeholder="https://github.com/yourusername" className="flex-1" data-id="element-475" />
                  </div>
                  <div className="flex items-center" data-id="element-476">
                    <TwitterIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-477" />
                    <Input value={formData.twitter} onChange={e => setFormData({
                  ...formData,
                  twitter: e.target.value
                })} placeholder="https://twitter.com/yourusername" className="flex-1" data-id="element-478" />
                  </div>
                  <div className="flex items-center" data-id="element-479">
                    <LinkedinIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-480" />
                    <Input value={formData.linkedin} onChange={e => setFormData({
                  ...formData,
                  linkedin: e.target.value
                })} placeholder="https://linkedin.com/in/yourusername" className="flex-1" data-id="element-481" />
                  </div>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200" data-id="element-482">
                <Button variant="outline" onClick={handleSaveDraft} className="flex items-center justify-center" data-id="element-483">
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-484" />
                  Save as Draft
                </Button>
                <Button variant="primary" onClick={handleSubmitForApproval} className="flex items-center justify-center" data-id="element-485">
                  <SendIcon className="h-4 w-4 mr-2" data-id="element-486" />
                  Submit for Approval
                </Button>
                {profile && <Button variant="ghost" onClick={() => {
              setFormData(profile);
              setIsEditing(false);
            }} data-id="element-487">
                    Cancel
                  </Button>}
              </div>
            </div> : profile ? <div className="space-y-6" data-id="element-488">
              {/* View Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-489">
                <div data-id="element-490">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-491">
                    Name
                  </label>
                  <p className="text-gray-900" data-id="element-492">{profile.name}</p>
                </div>
                <div data-id="element-493">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-494">
                    Title
                  </label>
                  <p className="text-gray-900" data-id="element-495">{profile.title}</p>
                </div>
              </div>
              <div data-id="element-496">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-497">
                  Bio
                </label>
                <p className="text-gray-900" data-id="element-498">{profile.bio}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-499">
                <div data-id="element-500">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-501">
                    Location
                  </label>
                  <p className="text-gray-900 flex items-center" data-id="element-502">
                    <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-503" />
                    {profile.location}, {profile.country}
                  </p>
                </div>
                <div data-id="element-504">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-505">
                    Rate
                  </label>
                  <p className="text-gray-900" data-id="element-506">
                    {profile.rateCurrency} ${profile.rateMin}-${profile.rateMax}
                    /hour
                  </p>
                </div>
              </div>
              <div data-id="element-507">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-508">
                  Skills & Certifications
                </label>
                <div className="space-y-2" data-id="element-509">
                  {profile.skills.map(skill => <div key={skill.name} className="flex items-center gap-2 p-2 bg-blue-50 rounded" data-id="element-510">
                      <span className="font-medium text-blue-900" data-id="element-511">
                        {skill.name}
                      </span>
                      {skill.certificationUrl && <a href={skill.certificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800 text-sm" data-id="element-512">
                          <AwardIcon className="h-3 w-3 mr-1" data-id="element-513" />
                          View Certificate
                        </a>}
                    </div>)}
                </div>
              </div>
              {profile.projects.length > 0 && <div data-id="element-514">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-515">
                    Projects
                  </label>
                  <div className="space-y-3" data-id="element-516">
                    {profile.projects.map((project, index) => <div key={index} className="p-3 bg-gray-50 rounded-lg" data-id="element-517">
                        <div className="flex items-center gap-2 mb-1" data-id="element-518">
                          <FolderIcon className="h-4 w-4 text-gray-400" data-id="element-519" />
                          <span className="font-medium text-gray-900" data-id="element-520">
                            {project.title}
                          </span>
                        </div>
                        <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-521">
                          <LinkIcon className="h-3 w-3 mr-1" data-id="element-522" />
                          {project.url}
                        </a>
                        {project.description && <p className="text-sm text-gray-600 mt-1" data-id="element-523">
                            {project.description}
                          </p>}
                      </div>)}
                  </div>
                </div>}
              <div data-id="element-524">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-525">
                  Open To
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-526">
                  {profile.openTo.map(option => <span key={option} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-527">
                      {option}
                    </span>)}
                </div>
              </div>
              {(profile.website || profile.github || profile.twitter || profile.linkedin) && <div data-id="element-528">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-529">
                    Links
                  </label>
                  <div className="space-y-2" data-id="element-530">
                    {profile.website && <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-531">
                        <GlobeIcon className="h-4 w-4 mr-2" data-id="element-532" />
                        Website
                      </a>}
                    {profile.github && <a href={profile.github} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-533">
                        <GithubIcon className="h-4 w-4 mr-2" data-id="element-534" />
                        GitHub
                      </a>}
                    {profile.twitter && <a href={profile.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-535">
                        <TwitterIcon className="h-4 w-4 mr-2" data-id="element-536" />
                        Twitter
                      </a>}
                    {profile.linkedin && <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-537">
                        <LinkedinIcon className="h-4 w-4 mr-2" data-id="element-538" />
                        LinkedIn
                      </a>}
                  </div>
                </div>}
            </div> : <div className="text-center py-12" data-id="element-539">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-540" />
              <p className="text-gray-600" data-id="element-541">No profile created yet</p>
            </div>}
        </Card>
      </div>
    </div>;
};
export default DeveloperDashboard;